import React, { useState } from 'react';
import { getElementDefinition } from './elementRegistry';
import { X, Box, Trash2 } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import './styles/ElementProperties.css';

/**
 * ElementProperties - Property editor panel for selected elements
 * Shows editable properties based on element type
 */
const ElementProperties = ({
  element,
  onUpdate,
  onDelete,
  onClose
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!element) {
    return (
      <div className="element-properties-empty">
        <p>Select an element to edit its properties</p>
      </div>
    );
  }

  const definition = getElementDefinition(element.type);

  const handlePropertyChange = (key, value) => {
    if (definition.isContainer) {
      onUpdate({
        ...element,
        props: {
          ...element.props,
          [key]: value
        }
      });
    } else {
      onUpdate({
        ...element,
        content: {
          ...element.content,
          [key]: value
        }
      });
    }
  };

  const handleDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  return (
    <div className="element-properties">
      {/* Header */}
      <div className="properties-header">
        <div className="properties-title">
          <span className="properties-icon">{definition?.icon || <Box size={24} />}</span>
          <h3>{definition?.label || element.type}</h3>
        </div>
        <button className="properties-close" onClick={onClose} title="Close" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <X size={20} />
        </button>
      </div>

      {/* Properties */}
      <div className="properties-body">
        {/* Element Type Info */}
        <div className="property-group">
          <label className="property-label">Element Type</label>
          <div className="property-value-display">{element.type}</div>
        </div>

        {/* Render properties based on schema */}
        {definition?.schema && renderProperties(element, definition, handlePropertyChange)}

        {/* Element ID (read-only) */}
        <div className="property-group">
          <label className="property-label">Element ID</label>
          <code className="property-value-code">{element.id}</code>
        </div>
      </div>

      {/* Footer with Delete Button */}
      <div className="properties-footer">
        <button
          className="properties-delete-button"
          onClick={() => setShowDeleteConfirm(true)}
          title="Delete element and all children"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
        >
          <Trash2 size={16} /> Delete Element
        </button>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Element"
        message={`Delete this ${element.type} element and all its children?`}
        details="This action cannot be undone."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

/**
 * Render property editors based on element schema
 */
const renderProperties = (element, definition, onChange) => {
  const schema = definition.schema;
  const data = definition.isContainer ? element.props : element.content;

  return Object.entries(schema).map(([key, config]) => {
    const value = data?.[key];

    return (
      <div key={key} className="property-group">
        <label className="property-label">
          {formatLabel(key)}
        </label>
        {renderPropertyInput(key, value, config, onChange)}
      </div>
    );
  });
};

/**
 * Render appropriate input based on property type
 */
const renderPropertyInput = (key, value, config, onChange) => {
  switch (config.type) {
    case 'text':
      return (
        <input
          type="text"
          className="property-input"
          value={value || ''}
          onChange={(e) => onChange(key, e.target.value)}
          placeholder={config.placeholder || `Enter ${formatLabel(key).toLowerCase()}`}
        />
      );

    case 'textarea':
      return (
        <textarea
          className="property-textarea"
          value={value || ''}
          onChange={(e) => onChange(key, e.target.value)}
          placeholder={config.placeholder || `Enter ${formatLabel(key).toLowerCase()}`}
          rows={config.rows || 3}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className="property-input"
          value={value || 0}
          onChange={(e) => onChange(key, parseInt(e.target.value))}
          min={config.min}
          max={config.max}
        />
      );

    case 'select':
      return (
        <select
          className="property-select"
          value={value || config.options[0]}
          onChange={(e) => onChange(key, e.target.value)}
        >
          {config.options.map(option => (
            <option key={option} value={option}>
              {formatLabel(option)}
            </option>
          ))}
        </select>
      );

    case 'color':
      return (
        <div className="property-color-picker">
          <input
            type="color"
            className="property-color-input"
            value={value || '#000000'}
            onChange={(e) => onChange(key, e.target.value)}
          />
          <input
            type="text"
            className="property-input property-color-text"
            value={value || '#000000'}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder="#000000"
          />
        </div>
      );

    case 'checkbox':
      return (
        <label className="property-checkbox-label">
          <input
            type="checkbox"
            className="property-checkbox"
            checked={value || false}
            onChange={(e) => onChange(key, e.target.checked)}
          />
          <span>{config.label || 'Enable'}</span>
        </label>
      );

    case 'boolean':
      return (
        <label className="property-checkbox-label">
          <input
            type="checkbox"
            className="property-checkbox"
            checked={value || false}
            onChange={(e) => onChange(key, e.target.checked)}
          />
          <span>{config.label || formatLabel(key)}</span>
        </label>
      );

    case 'array':
      // For arrays (like dropdown options), display as comma-separated text
      const arrayValue = Array.isArray(value) ? value.join(', ') : (value || '');
      return (
        <textarea
          className="property-textarea"
          value={arrayValue}
          onChange={(e) => {
            const newValue = e.target.value.split(',').map(v => v.trim()).filter(v => v);
            onChange(key, newValue);
          }}
          placeholder="Enter options separated by commas"
          rows={3}
        />
      );

    case 'richtext':
      // For now, use textarea for richtext - can be enhanced with a rich text editor later
      return (
        <textarea
          className="property-textarea"
          value={value || ''}
          onChange={(e) => onChange(key, e.target.value)}
          placeholder="Enter HTML content"
          rows={5}
        />
      );

    case 'image-upload':
    case 'image-array':
      // Placeholder for image upload functionality
      return (
        <div className="property-value-display">
          <p>Image upload coming soon</p>
          <input
            type="text"
            className="property-input"
            value={value || ''}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder="Image URL"
            style={{ marginTop: '0.5rem' }}
          />
        </div>
      );

    default:
      return (
        <input
          type="text"
          className="property-input"
          value={value || ''}
          onChange={(e) => onChange(key, e.target.value)}
        />
      );
  }
};

/**
 * Format property keys into readable labels
 */
const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/_/g, ' ');
};

export default ElementProperties;
